import { useMemo } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/apiClient";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  FileText, Users, Euro, TrendingUp, AlertTriangle, Clock,
  Plus, ArrowRight, CheckCircle2, XCircle, Send
} from "lucide-react";
import { Link } from "react-router-dom";
import { format, startOfMonth, endOfMonth, subDays, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";
import type { Invoice, BillingCustomer } from "@/types/billing";

const statusColors: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700",
  issued: "bg-blue-100 text-blue-700",
  sent: "bg-indigo-100 text-indigo-700",
  paid: "bg-green-100 text-green-700",
  partial: "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-700",
  rectified: "bg-purple-100 text-purple-700",
};

const statusLabels: Record<string, string> = {
  draft: "Borrador",
  issued: "Emitida",
  sent: "Enviada",
  paid: "Cobrada",
  partial: "Parcial",
  cancelled: "Anulada",
  rectified: "Rectificada",
};

export default function BillingDashboard() {
  const { user } = useAuth();

  const monthStart = useMemo(() => startOfMonth(new Date()).toISOString(), []);
  const monthEnd = useMemo(() => endOfMonth(new Date()).toISOString(), []);
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);

  // Fetch invoices for stats
  const { data: invoices = [] } = useQuery<Invoice[]>({
    queryKey: ["billing-invoices-dashboard"],
    queryFn: async () => {
      const { data, error } = await api
        .from("invoices")
        .select("*")
        .order("issue_date", { ascending: false })
        .limit(100);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  // Fetch recent invoices
  const { data: recentInvoices = [] } = useQuery<Invoice[]>({
    queryKey: ["billing-recent-invoices"],
    queryFn: async () => {
      const { data, error } = await api
        .from("invoices")
        .select(`
          *,
          customer:billing_customers(id, legal_name, trade_name)
        `)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user,
  });

  // Fetch customers count
  const { data: customersData } = useQuery({
    queryKey: ["billing-customers-count"],
    queryFn: async () => {
      const { count, error } = await api
        .from("billing_customers")
        .select("id", { count: "exact", head: true })
        .eq("is_active", true);
      if (error) throw error;
      return count || 0;
    },
    enabled: !!user,
  });

  // Calculate stats
  const stats = useMemo(() => {
    const thisMonth = invoices.filter(i =>
      i.issue_date >= monthStart.split('T')[0] &&
      i.status !== 'cancelled' && i.status !== 'draft'
    );
    const pending = invoices.filter(i =>
      ['issued', 'sent', 'partial'].includes(i.status)
    );
    const overdue = pending.filter(i =>
      i.due_date && i.due_date < today
    );

    return {
      totalInvoicedMonth: thisMonth.reduce((sum, i) => sum + (i.total_amount || 0), 0),
      totalPending: pending.reduce((sum, i) => sum + (i.total_amount - i.paid_amount), 0),
      totalOverdue: overdue.reduce((sum, i) => sum + (i.total_amount - i.paid_amount), 0),
      invoicesThisMonth: thisMonth.length,
      pendingCount: pending.length,
      overdueCount: overdue.length,
      customersCount: customersData || 0,
    };
  }, [invoices, monthStart, today, customersData]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Facturación</h1>
            <p className="text-muted-foreground">
              Gestión de facturas, clientes y contabilidad
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <Link to="/billing/customers">
                <Users className="w-4 h-4 mr-2" />
                Clientes
              </Link>
            </Button>
            <Button asChild>
              <Link to="/billing/invoices/new">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Factura
              </Link>
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Facturado este mes</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(stats.totalInvoicedMonth)}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats.invoicesThisMonth} facturas emitidas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Pendiente de cobro</CardTitle>
              <Clock className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-600">
                {formatCurrency(stats.totalPending)}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats.pendingCount} facturas pendientes
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Vencido</CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {formatCurrency(stats.totalOverdue)}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats.overdueCount} facturas vencidas
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Clientes</CardTitle>
              <Users className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.customersCount}</div>
              <p className="text-xs text-muted-foreground">clientes activos</p>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="hover:shadow-md transition-shadow cursor-pointer" asChild>
            <Link to="/billing/invoices">
              <CardContent className="flex items-center gap-4 p-6">
                <div className="p-3 rounded-full bg-blue-100">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">Facturas</h3>
                  <p className="text-sm text-muted-foreground">Ver todas las facturas</p>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </CardContent>
            </Link>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" asChild>
            <Link to="/billing/customers">
              <CardContent className="flex items-center gap-4 p-6">
                <div className="p-3 rounded-full bg-green-100">
                  <Users className="h-6 w-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">Clientes</h3>
                  <p className="text-sm text-muted-foreground">Gestionar clientes</p>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </CardContent>
            </Link>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer" asChild>
            <Link to="/billing/settings">
              <CardContent className="flex items-center gap-4 p-6">
                <div className="p-3 rounded-full bg-purple-100">
                  <Euro className="h-6 w-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold">Configuración</h3>
                  <p className="text-sm text-muted-foreground">Datos empresa y VeriFactu</p>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </CardContent>
            </Link>
          </Card>
        </div>

        {/* Recent Invoices */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Facturas Recientes</CardTitle>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/billing/invoices">
                Ver todas <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Número</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Importe</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>VeriFactu</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No hay facturas. <Link to="/billing/invoices/new" className="text-primary underline">Crear primera factura</Link>
                    </TableCell>
                  </TableRow>
                ) : (
                  recentInvoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell>
                        <Link
                          to={`/billing/invoices/${invoice.id}`}
                          className="font-mono font-medium hover:underline"
                        >
                          {invoice.invoice_number || 'Borrador'}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {(invoice as any).customer?.trade_name ||
                         (invoice as any).customer?.legal_name ||
                         invoice.customer_name}
                      </TableCell>
                      <TableCell>
                        {format(new Date(invoice.issue_date), "dd/MM/yyyy")}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(invoice.total_amount)}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[invoice.status]}>
                          {statusLabels[invoice.status]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {invoice.verifactu_status === 'accepted' ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : invoice.verifactu_status === 'rejected' ? (
                          <XCircle className="w-4 h-4 text-red-500" />
                        ) : invoice.verifactu_status === 'submitted' ? (
                          <Send className="w-4 h-4 text-blue-500" />
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Pending/Overdue Alert */}
        {stats.overdueCount > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="flex items-center gap-4 p-4">
              <AlertTriangle className="h-8 w-8 text-red-500" />
              <div className="flex-1">
                <h3 className="font-semibold text-red-700">
                  {stats.overdueCount} factura{stats.overdueCount !== 1 ? 's' : ''} vencida{stats.overdueCount !== 1 ? 's' : ''}
                </h3>
                <p className="text-sm text-red-600">
                  Total pendiente: {formatCurrency(stats.totalOverdue)}
                </p>
              </div>
              <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-100" asChild>
                <Link to="/billing/invoices?status=overdue">
                  Ver facturas vencidas
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
